import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import WeeklyScripture from '@/components/WeeklyScripture';
import PersonalFocusPath from '@/components/PersonalFocusPath';
import INeedSupportButton from '@/components/INeedSupportButton';
import { QuietRecognitionCard, ReflectionPrompt } from '@/components/QuietRecognition';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  ArrowRight, Users, Video, MessageSquare, Calendar, Clock,
  Sparkles, Crown, Palmtree, Heart, Lock, Play, FileText, Music, Volume2, VolumeX, Star, Diamond, Target,
  BookOpen, Compass, CheckCircle, CreditCard, Shield, Check, Loader2, Mail, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline Trial Signup Component
const InnerCircleTrialSignup = ({ onTrialStarted, celebrate }) => {
  const { user, refreshUser } = useAuth();
  const [showCheckout, setShowCheckout] = useState(false);
  
  return (
    <div className="space-y-6 mb-8">
      {/* Welcome Header */}
      <Card className="card-base border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold text-forest mb-2">
                You deserve a space where you don't have to prove anything, {user?.full_name?.split(' ')[0] || 'Sis'} 💎
              </h3>
              <p className="text-gray-600">
                I'm <span className="font-semibold text-purple-600">SHE</span> — your digital guide. 
                You've joined a sisterhood of brilliant Black women who see you, celebrate you, and hold space for your becoming.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Welcome Video Placeholder */}
      <Card className="card-base overflow-hidden">
        <div className="relative aspect-video bg-gradient-to-br from-forest/10 to-terracotta/10 flex items-center justify-center">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22%231a3c34%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M0%200h20v20H0V0zm20%2020h20v20H20V20z%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
          <div className="relative text-center p-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-terracotta/20 flex items-center justify-center">
              <Play className="h-10 w-10 text-terracotta ml-1" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-forest mb-2">
              A Soul Welcome From Crystal & Dr. Nicole
            </h3>
            <p className="text-gray-600 text-sm max-w-md mx-auto">
              You've already done more than most will ever try. But now? It's your turn. Hear what's waiting for you inside.
            </p>
            <Button 
              variant="outline" 
              className="mt-4 border-terracotta text-terracotta hover:bg-terracotta/10"
              onClick={() => toast.info('Your welcome message is coming soon, Sis!')}
            >
              <Play className="h-4 w-4 mr-2" />
              Receive Your Welcome
            </Button>
          </div>
        </div>
      </Card>

      {/* Benefits + Checkout in One Card */}
      <Card className="card-base overflow-hidden">
        <div className="bg-gradient-to-r from-forest to-forest/90 text-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-6 w-6" />
            <h2 className="font-heading text-2xl font-bold">Your Inner Circle Awaits</h2>
          </div>
          <p className="text-white/80">You don't have to carry it all alone. Step into sisterhood.</p>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold">$97</span>
            <span className="text-white/70">/month after 30-day trial</span>
          </div>
        </div>
        
        <CardContent className="p-6">
          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {[
              { icon: Users, label: 'Sacred Sisterhood', desc: 'A safe space where you are truly seen' },
              { icon: BookOpen, label: 'Weekly Soul Food', desc: 'Wisdom to nourish your spirit' },
              { icon: Video, label: 'The Vault', desc: 'Content designed for your wholeness' },
              { icon: Calendar, label: 'Live Gatherings', desc: 'Monthly circles with Crystal & Dr. Nicole' },
              { icon: Star, label: 'Unexpected Blessings', desc: 'Rewards for showing up for yourself' },
              { icon: Compass, label: '90-Day Becoming', desc: "You're not behind. You're becoming." }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-sand/30">
                <item.icon className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-forest text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Toggle to Show/Hide Checkout */}
          {!showCheckout ? (
            <Button 
              onClick={() => setShowCheckout(true)}
              className="w-full bg-terracotta hover:bg-terracotta/90 text-white py-6 text-lg"
              data-testid="start-trial-btn"
            >
              <Heart className="h-5 w-5 mr-2" />
              Say Yes to Yourself
            </Button>
          ) : (
            <MockCheckoutForm 
              user={user} 
              refreshUser={refreshUser}
              onSuccess={onTrialStarted}
              onCancel={() => setShowCheckout(false)}
              celebrate={celebrate}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Mock Checkout Form (until Stripe is configured)
const MockCheckoutForm = ({ user, refreshUser, onSuccess, onCancel, celebrate }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!cardNumber || !expiry || !cvc) {
      setError('Please fill in all card details');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      // Use mock endpoint
      await axios.post(
        `${API_URL}/api/subscription/start-trial-mock`,
        { tier: 'inner_circle', payment_method_id: 'mock_pm' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Celebration animation!
      if (celebrate) {
        celebrate('confetti', 'Welcome home, Sis! Your journey of becoming begins now.');
      }
      await refreshUser();
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || err.message || 'Something went wrong');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-2">
          <Check className="h-4 w-4" />
          You're allowed to receive this:
        </div>
        <ul className="text-sm text-green-600 space-y-1 ml-6">
          <li>• 30 days to exhale, explore, and belong</li>
          <li>• Permission to pause anytime</li>
          <li>• No charge until {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</li>
        </ul>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-forest mb-2">
            <CreditCard className="h-4 w-4 inline mr-1" />
            Card Number
          </label>
          <input
            type="text"
            placeholder="4242 4242 4242 4242"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            className="w-full p-3 border border-sand rounded-xl bg-white focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-forest mb-2">Expiry</label>
            <input
              type="text"
              placeholder="MM/YY"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="w-full p-3 border border-sand rounded-xl bg-white focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-forest mb-2">CVC</label>
            <input
              type="text"
              placeholder="123"
              value={cvc}
              onChange={(e) => setCvc(e.target.value)}
              className="w-full p-3 border border-sand rounded-xl bg-white focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Shield className="h-3 w-3" />
          Test mode — enter any card details
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={processing}>
          Back
        </Button>
        <Button type="submit" disabled={processing} className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white">
          {processing ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening the door...</>
          ) : (
            <><Calendar className="h-4 w-4 mr-2" />Step Into Your Becoming</>
          )}
        </Button>
      </div>
    </form>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, hasInnerCircleAccess, hasCoachAccess, hasSDCAccess, hasCostaRicaAccess, hasTier } = useAuth();
  const { celebrate } = useCelebration();
  const [dashboard, setDashboard] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState({ wonder_twin_sessions: [], group_sessions: [] });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchedCoach, setMatchedCoach] = useState(null);
  
  // Focus Path state for 90-day onboarding
  const [showFocusPathDialog, setShowFocusPathDialog] = useState(false);
  const [focusPath, setFocusPath] = useState(null);
  const [focusPathLoading, setFocusPathLoading] = useState(true);
  
  // Weekly Digest state
  const [weeklyDigest, setWeeklyDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  
  // Book recommendations state
  const [recommendedBooks, setRecommendedBooks] = useState([]);

  // Check if user is Inner Edge only (must use assigned coach)
  const isInnerEdgeOnly = user && hasTier('inner_edge') && !hasTier('sdc') && !hasTier('coaching_activation');
  
  // Check if user is admin or coach (not a member going through the journey)
  // CRITICAL: Only calculate these values when user is fully loaded
  const isAdminOrCoach = user?.role === 'admin' || user?.role === 'coach';
  const isMember = user && !isAdminOrCoach;
  
  // CRITICAL: Don't show ANY role-specific content until auth is fully loaded
  const showMemberContent = !authLoading && user && isMember;
  const showAdminContent = !authLoading && user && isAdminOrCoach;

  useEffect(() => {
    fetchDashboard();
    fetchUpcomingSessions();
    fetchActivities();
    fetchRecommendedBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate useEffect for focus path that depends on user role
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'coach') {
      fetchFocusPath();
    } else if (user) {
      // User is admin/coach, no need to fetch focus path
      setFocusPathLoading(false);
    }
    if (isInnerEdgeOnly) {
      fetchMatchedCoach();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, isInnerEdgeOnly]);

  const fetchMatchedCoach = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagnostic/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const coachData = response.data?.coach_match?.coach;
      if (coachData) {
        setMatchedCoach(coachData);
      }
    } catch (error) {
      console.error('Failed to fetch matched coach:', error);
    }
  };

  const fetchFocusPath = async () => {
    // Skip for admins and coaches
    if (isAdminOrCoach) {
      setFocusPathLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/focus-path`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.has_focus_path) {
        setFocusPath(response.data.focus_path);
      } else {
        // Show focus path dialog for new members who haven't set their focus
        // Only if they have inner circle or higher access AND are not admin/coach
        const hasAccess = user?.active_tiers?.some(t => 
          ['inner_circle', 'inner_edge', 'coaching_activation', 'sdc'].includes(t)
        );
        if (hasAccess && !localStorage.getItem('focusPathDismissed')) {
          // Small delay to let the page load first
          setTimeout(() => setShowFocusPathDialog(true), 1500);
        }
      }
    } catch (error) {
      console.error('Failed to fetch focus path:', error);
    } finally {
      setFocusPathLoading(false);
    }
  };

  const handleFocusPathComplete = (selectedFocus) => {
    setFocusPath({
      focus_area: selectedFocus.id,
      focus_label: selectedFocus.label,
      day_number: 1,
      progress_percent: 1.1
    });
  };

  const handleFocusPathDismiss = () => {
    setShowFocusPathDialog(false);
    // Remember dismissal for this session
    localStorage.setItem('focusPathDismissed', 'true');
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/upcoming-sessions`);
      setUpcomingSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch upcoming sessions:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  const fetchRecommendedBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/books/recommended`);
      setRecommendedBooks(response.data.books || []);
    } catch (error) {
      console.error('Failed to fetch recommended books:', error);
    }
  };

  const formatSessionDate = (dateStr, timeStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return `${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${timeStr || 'TBD'}`;
  };

  // Weekly Digest functions
  const fetchWeeklyDigest = async () => {
    setDigestLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/journal/weekly-digest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyDigest(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly digest:', error);
    } finally {
      setDigestLoading(false);
    }
  };

  const sendDigestEmail = async () => {
    setSendingDigest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/journal/weekly-digest/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Weekly digest sent to your email!');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingDigest(false);
    }
  };

  const activeTiers = user?.active_tiers || [];
  const hasAnyTier = activeTiers.length > 0;

  const tierBadges = {
    inner_circle: { label: "Inner Circle", icon: Heart, color: "bg-terracotta" },
    inner_edge: { label: "Inner Edge", icon: Sparkles, color: "bg-forest" },
    costa_rica: { label: "Costa Rica", icon: Palmtree, color: "bg-terracotta" },
    coaching_activation: { label: "Signature Coaching", icon: Users, color: "bg-forest" },
    sdc: { label: "SDC VIP", icon: Crown, color: "bg-terracotta" }
  };

  // CRITICAL: Show loading spinner while auth is loading to prevent flash of wrong content
  if (authLoading || !user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="dashboard-loading">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-terracotta mx-auto mb-4" />
            <p className="text-gray-500">Loading your dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Personal Focus Path Dialog for new members (not admins/coaches) */}
      {showMemberContent && (
        <PersonalFocusPath 
          isOpen={showFocusPathDialog} 
          onClose={handleFocusPathDismiss}
          onComplete={handleFocusPathComplete}
        />
      )}
      
      <div className="p-6 lg:p-8" data-testid="dashboard-page">
        {/* Welcome Banner Image */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg relative h-48 md:h-56">
          <img 
            src="https://customer-assets.emergentagent.com/job_serene-edison/artifacts/btantxd6_8.png"
            alt="Sister Diamonds community"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center">
            <div className="p-6 md:p-8">
              <p className="text-white text-sm md:text-base font-medium opacity-90">Sister Diamonds Collective</p>
              <p className="text-white text-xl md:text-2xl font-heading font-bold">Your Space to Exhale &amp; Soar</p>
            </div>
          </div>
        </div>

        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
              Welcome home{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ', Sis'}
            </h1>
            {showAdminContent && (
              <Badge className="bg-forest text-white">
                {user?.role === 'admin' ? 'Administrator' : 'Coach'}
              </Badge>
            )}
            {showMemberContent && activeTiers.map(tier => {
              const badge = tierBadges[tier];
              if (!badge) return null;
              return (
                <Badge key={tier} className={`${badge.color} text-white`}>
                  <badge.icon className="h-3 w-3 mr-1" />
                  {badge.label}
                </Badge>
              );
            })}
          </div>
          <p className="text-gray-600">
            {showAdminContent
              ? "Manage the Sister Diamonds platform from your dashboard."
              : hasAnyTier 
                ? "Exhale here. This is your sacred space to rest, reflect, and rise."
                : "You're already qualified. The next level just needs your 'yes.'"}
          </p>
        </div>

        {/* Focus Path Journey Progress Card - Members only */}
        {showMemberContent && focusPath && hasAnyTier && (
          <Card className="card-base mb-8 border-l-4 border-l-terracotta bg-gradient-to-r from-terracotta/5 to-transparent">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-terracotta/10 flex items-center justify-center">
                    <Target className="h-6 w-6 text-terracotta" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Your Sacred Focus</p>
                    <h3 className="font-heading text-lg font-semibold text-forest">{focusPath.focus_label}</h3>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-terracotta">Day {focusPath.day_number}</p>
                  <p className="text-xs text-gray-500">of your becoming</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>You're not behind. You're becoming.</span>
                  <span>{focusPath.progress_percent}%</span>
                </div>
                <div className="h-2 bg-sand rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-terracotta to-terracotta/70 rounded-full transition-all duration-500"
                    style={{ width: `${focusPath.progress_percent}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* What's Next - Guidance Card (shown when focus path is set) - Members only */}
        {showMemberContent && focusPath && hasAnyTier && (
          <Card className="card-base mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Compass className="h-5 w-5 text-terracotta" />
                Where Does Your Soul Need a Pause Today?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/communities" className="group">
                  <div className="p-4 rounded-xl bg-forest/5 border border-forest/10 hover:border-forest/30 transition-all">
                    <Users className="h-6 w-6 text-forest mb-2" />
                    <p className="font-medium text-forest text-sm">Find Your Circle</p>
                    <p className="text-xs text-gray-500 mt-1">A safe space to speak what's sacred</p>
                  </div>
                </Link>
                <Link to="/journal" className="group">
                  <div className="p-4 rounded-xl bg-terracotta/5 border border-terracotta/10 hover:border-terracotta/30 transition-all">
                    <BookOpen className="h-6 w-6 text-terracotta mb-2" />
                    <p className="font-medium text-forest text-sm">Soul Check-In</p>
                    <p className="text-xs text-gray-500 mt-1">What's rising in your heart right now?</p>
                  </div>
                </Link>
                <Link to="/videos" className="group">
                  <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 hover:border-purple-300 transition-all">
                    <Video className="h-6 w-6 text-purple-600 mb-2" />
                    <p className="font-medium text-forest text-sm">Rest & Restore</p>
                    <p className="text-xs text-gray-500 mt-1">Wisdom for your wholeness</p>
                  </div>
                </Link>
                <Link to="/settings" className="group">
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 hover:border-amber-300 transition-all">
                    <CheckCircle className="h-6 w-6 text-amber-600 mb-2" />
                    <p className="font-medium text-forest text-sm">Share Your Story</p>
                    <p className="text-xs text-gray-500 mt-1">Let us see the real you</p>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* I Need Support Card - Members only */}
        {showMemberContent && hasAnyTier && (
          <INeedSupportButton variant="card" className="mb-8" />
        )}

        {/* Quiet Recognition - Gentle Affirmation - Members only */}
        {showMemberContent && hasAnyTier && focusPath && (
          <div className="mb-8">
            <QuietRecognitionCard focusArea={focusPath?.focus_area || 'general'} />
          </div>
        )}

        {/* Morning/Evening Reflection - Members only */}
        {showMemberContent && hasAnyTier && focusPath && (
          <div className="mb-8">
            <ReflectionPrompt 
              timeOfDay={new Date().getHours() < 12 ? 'morning' : 'evening'}
              focusArea={focusPath?.focus_area || 'general'}
            />
          </div>
        )}

        {/* Weekly Journal Digest Card - Members only */}
        {showMemberContent && hasAnyTier && (
          <Card className="card-base mb-8 overflow-hidden" data-testid="weekly-digest-card">
            <div className="bg-gradient-to-r from-forest/10 via-purple-50/50 to-terracotta/10 p-1">
              <CardContent className="bg-white rounded-lg p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-forest to-purple-600 flex items-center justify-center shrink-0">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
                        Your Weekly Journal Digest
                        <Badge className="bg-forest/10 text-forest text-xs">AI-Powered</Badge>
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Get a personalized summary of your journaling journey this week, sent to your email.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={fetchWeeklyDigest}
                      disabled={digestLoading}
                      className="shrink-0"
                    >
                      {digestLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>View Digest</>
                      )}
                    </Button>
                    <Button 
                      onClick={sendDigestEmail}
                      disabled={sendingDigest || digestLoading}
                      className="bg-forest hover:bg-forest/90 text-white shrink-0"
                    >
                      {sendingDigest ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><Mail className="h-4 w-4 mr-2" />Email Me</>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Display digest preview when loaded */}
                {weeklyDigest && weeklyDigest.has_digest && (
                  <div className="mt-5 pt-5 border-t border-gray-100 space-y-4">
                    <div className="p-4 bg-purple-50/50 rounded-lg">
                      <p className="text-forest font-medium">{weeklyDigest.greeting}</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 uppercase mb-1">This Week's Highlight</p>
                        <p className="text-sm text-forest">{weeklyDigest.highlight}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 uppercase mb-1">Growth Noticed</p>
                        <p className="text-sm text-forest">{weeklyDigest.growth_noticed}</p>
                      </div>
                    </div>
                    <div className="p-4 bg-forest/5 rounded-lg border border-forest/10 text-center">
                      <p className="text-sm text-forest/70 mb-1">Your Truth This Week</p>
                      <p className="text-forest font-heading">"{weeklyDigest.affirmation}"</p>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      📊 {weeklyDigest.entries_count} journal {weeklyDigest.entries_count === 1 ? 'entry' : 'entries'} this week
                    </p>
                  </div>
                )}
                
                {weeklyDigest && !weeklyDigest.has_digest && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-sm text-amber-800">
                      <BookOpen className="h-4 w-4 inline mr-2" />
                      {weeklyDigest.message}
                    </p>
                  </div>
                )}
              </CardContent>
            </div>
          </Card>
        )}

        {/* Prompt to set focus path if not set - Members only */}
        {showMemberContent && !focusPath && !focusPathLoading && hasAnyTier && (
          <Card className="card-base mb-8 border-dashed border-2 border-terracotta/30 bg-terracotta/5">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-terracotta/20 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-terracotta" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-forest">Start Your 90-Day Journey</h3>
                    <p className="text-sm text-gray-600">Set a focus intention to personalize your experience</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowFocusPathDialog(true)}
                  className="bg-terracotta hover:bg-terracotta/90 text-white"
                  data-testid="set-focus-path-btn"
                >
                  Choose My Focus <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Tier - Welcome & Trial Signup Section - Members only */}
        {showMemberContent && !hasAnyTier && (
          <InnerCircleTrialSignup 
            celebrate={celebrate}
            onTrialStarted={() => {
              // After trial starts, show the focus path dialog
              setTimeout(() => setShowFocusPathDialog(true), 500);
            }}
          />
        )}

        {/* Upcoming Sessions Section */}
        {hasAnyTier && (upcomingSessions.wonder_twin_sessions.length > 0 || upcomingSessions.group_sessions.length > 0) && (
          <Card className="card-base mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Calendar className="h-5 w-5 text-terracotta" />
                Upcoming Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Wonder Twin Sessions */}
                {upcomingSessions.wonder_twin_sessions.map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-3 bg-terracotta/5 rounded-lg border border-terracotta/20">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-terracotta/10 flex items-center justify-center">
                        <Crown className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <h4 className="font-medium text-forest">{session.title || 'Wonder Twin Session'}</h4>
                        <p className="text-sm text-gray-500">{formatSessionDate(session.scheduled_date, session.scheduled_time)}</p>
                      </div>
                    </div>
                    {session.zoom_link && (
                      <a href={session.zoom_link} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="btn-primary">
                          Join <Video className="h-4 w-4 ml-1" />
                        </Button>
                      </a>
                    )}
                  </div>
                ))}
                {/* Group Sessions */}
                {upcomingSessions.group_sessions.map((session) => {
                  // Check if session is within 5 minutes of starting AND not more than 2 hours past start
                  const sessionDateTime = new Date(`${session.scheduled_date}T${session.scheduled_time || '00:00'}`);
                  const now = new Date();
                  const minutesUntilSession = (sessionDateTime - now) / (1000 * 60);
                  const minutesSinceStart = -minutesUntilSession; // Positive if session has started
                  
                  // Can join: 5 min before start up to 2 hours (120 min) after start
                  const canJoin = minutesUntilSession <= 5 && minutesSinceStart <= 120;
                  const sessionExpired = minutesSinceStart > 120;
                  
                  return (
                    <div key={session.id} className="flex items-center justify-between p-3 bg-forest/5 rounded-lg border border-forest/20">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-forest/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-forest" />
                        </div>
                        <div>
                          <h4 className="font-medium text-forest">{session.title}</h4>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-500">{formatSessionDate(session.scheduled_date, session.scheduled_time)}</p>
                            <Badge variant="outline" className="text-xs">{session.session_type?.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      </div>
                      {session.zoom_link && (
                        sessionExpired ? (
                          <div className="text-right">
                            <Badge variant="secondary" className="text-xs text-gray-500">Session Ended</Badge>
                          </div>
                        ) : canJoin ? (
                          <a href={session.zoom_link} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="bg-forest text-white hover:bg-forest/90">
                              Join Now <Video className="h-4 w-4 ml-1" />
                            </Button>
                          </a>
                        ) : (
                          <div className="text-right">
                            <Button size="sm" variant="outline" disabled className="opacity-50">
                              <Clock className="h-4 w-4 mr-1" /> Join
                            </Button>
                            <p className="text-xs text-gray-500 mt-1">Available 5 min before</p>
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly Soul Food with Comments */}
        {hasAnyTier && <WeeklyScripture />}

        {/* Diamond Points Summary */}
        {hasAnyTier && user?.diamond_points > 0 && (
          <Card className="card-base mb-8">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Diamond className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-forest">Your Diamonds</p>
                    <p className="text-sm text-gray-500">Added to your joy bank</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-600">{user.diamond_points || 0}</p>
                  <Link to="/settings" className="text-xs text-terracotta hover:underline">
                    Claim your blessings →
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommended Reading */}
        {hasAnyTier && recommendedBooks.length > 0 && (
          <Card className="card-base mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-terracotta" />
                Soul Nourishment for Your Journey
              </CardTitle>
              <p className="text-sm text-gray-500">Rest is your birthright, not a reward. Here's wisdom for your becoming.</p>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendedBooks.slice(0, 3).map((book, idx) => (
                  <a 
                    key={idx}
                    href={book.amazon_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-amber-50/50 rounded-xl border border-amber-200/50 hover:border-terracotta/50 hover:shadow-md transition-all group"
                  >
                    <h4 className="font-medium text-forest group-hover:text-terracotta transition-colors line-clamp-1">
                      {book.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">by {book.author}</p>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{book.description}</p>
                    <Badge variant="outline" className="mt-3 text-xs bg-white">
                      {book.category}
                    </Badge>
                  </a>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Link to="/books" className="text-sm text-terracotta hover:underline">
                  View all book recommendations →
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity Feed */}
        {hasAnyTier && activities.length > 0 && (
          <Card className="card-base mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-terracotta" />
                Sisterhood Logged
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activities.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 bg-sand/30 rounded-lg">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Heart className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-forest text-sm">{activity.title}</h4>
                      <p className="text-xs text-gray-500">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(activity.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* This Week's Content (Inner Circle+) */}
        {hasInnerCircleAccess() && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Weekly Gems */}
            <Card className="card-base">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Play className="h-5 w-5 text-terracotta" />
                  This Week&apos;s Soul Food
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.this_week?.transmission ? (
                  <div>
                    <h3 className="font-medium text-forest">{dashboard.this_week.transmission.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                      {dashboard.this_week.transmission.content}
                    </p>
                    
                    {/* Audio Player */}
                    {dashboard.this_week.transmission.media_type === 'audio' && dashboard.this_week.transmission.audio_url && (
                      <div className="mt-3 p-3 bg-sand/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Music className="h-4 w-4 text-terracotta" />
                          <span className="text-sm font-medium text-forest">Listen to this transmission</span>
                        </div>
                        <audio 
                          controls 
                          className="w-full h-10"
                          style={{ borderRadius: '8px' }}
                        >
                          <source src={`${API_URL}${dashboard.this_week.transmission.audio_url}`} />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                    
                    {/* Video Player */}
                    {dashboard.this_week.transmission.media_type === 'video' && dashboard.this_week.transmission.video_url && (
                      <div className="mt-3 rounded-lg overflow-hidden">
                        <video 
                          controls 
                          className="w-full rounded-lg"
                          style={{ maxHeight: '300px' }}
                        >
                          <source src={`${API_URL}${dashboard.this_week.transmission.video_url}`} />
                          Your browser does not support the video element.
                        </video>
                      </div>
                    )}
                    
                    {/* Vimeo Embed */}
                    {dashboard.this_week.transmission.media_type === 'vimeo' && dashboard.this_week.transmission.vimeo_url && (
                      <div className="mt-3 rounded-lg overflow-hidden aspect-video">
                        <iframe
                          src={`https://player.vimeo.com/video/${dashboard.this_week.transmission.vimeo_url.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1]}?h=0&title=0&byline=0&portrait=0`}
                          className="w-full h-full rounded-lg"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title={dashboard.this_week.transmission.title}
                        />
                      </div>
                    )}
                    
                    {/* Fallback for transmissions without media */}
                    {!dashboard.this_week.transmission.media_type && (
                      <Button variant="link" className="text-terracotta p-0 mt-2">
                        Read Full Message <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">New soul food is being prepared for you...</p>
                )}
              </CardContent>
            </Card>

            {/* Weekly Prompt */}
            <Card className="card-base">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <FileText className="h-5 w-5 text-terracotta" />
                  What&apos;s Rising in Your Heart?
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.this_week?.prompt ? (
                  <div>
                    <h3 className="font-medium text-forest">{dashboard.this_week.prompt.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 italic">
                      &ldquo;{dashboard.this_week.prompt.prompt_text}&rdquo;
                    </p>
                    <Button 
                      variant="link" 
                      className="text-terracotta p-0 mt-2"
                      onClick={() => navigate('/journal', { state: { reflectionPrompt: dashboard.this_week.prompt } })}
                    >
                      Pour Out Your Heart <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">A new soul prompt is coming soon...</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Session Wallet (Coaching/Inner Edge) */}
        {(hasTier('coaching_activation') || hasTier('inner_edge')) && dashboard?.wallets && (
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">Your Session Wallet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {hasTier('coaching_activation') && (
                  <>
                    <div className="text-center p-4 bg-forest/5 rounded-xl">
                      <p className="text-3xl font-bold text-forest">{dashboard.wallets.coach_sessions}</p>
                      <p className="text-sm text-gray-600">Coach Sessions</p>
                    </div>
                    <div className="text-center p-4 bg-terracotta/5 rounded-xl">
                      <p className="text-3xl font-bold text-terracotta">{dashboard.wallets.founder_sessions}</p>
                      <p className="text-sm text-gray-600">Wonder Twin Sessions</p>
                    </div>
                  </>
                )}
                {hasTier('inner_edge') && (
                  <div className="text-center p-4 bg-forest/5 rounded-xl">
                    <p className="text-3xl font-bold text-forest">{dashboard.wallets.inner_edge_sessions}</p>
                    <p className="text-sm text-gray-600">Inner Edge Sessions</p>
                  </div>
                )}
              </div>

              {/* Inner Edge Only: Show assigned coach card */}
              {isInnerEdgeOnly && matchedCoach && (
                <div className="mt-6 p-4 bg-gradient-to-r from-forest/5 to-terracotta/5 rounded-xl border border-forest/20">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Avatar className="h-16 w-16 border-2 border-white shadow">
                        <AvatarImage src={matchedCoach.image_url} />
                        <AvatarFallback className="bg-forest text-white">
                          {matchedCoach.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <Badge className="absolute -top-1 -right-1 bg-terracotta text-white text-xs px-1.5">
                        <Star className="h-2.5 w-2.5" />
                      </Badge>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-terracotta font-medium uppercase tracking-wide">Your Primary Coach</p>
                      <h4 className="font-heading text-lg font-semibold text-forest">{matchedCoach.name}</h4>
                      <p className="text-sm text-gray-600">{matchedCoach.title}</p>
                    </div>
                    <Link to={`/coaches/${matchedCoach.id}`}>
                      <Button className="btn-primary" size="sm">
                        Book Session <Calendar className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Inner Edge Only: No matched coach yet */}
              {isInnerEdgeOnly && !matchedCoach && (
                <div className="mt-6 p-4 bg-sand/30 rounded-xl text-center">
                  <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Complete your diagnostic to get matched with your coach</p>
                  <Link to="/diagnostic">
                    <Button className="btn-outline" size="sm">
                      Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )}

              {/* Non-Inner Edge: Generic booking button */}
              {!isInnerEdgeOnly && (
                <div className="mt-4 flex justify-center">
                  <Link to="/sessions">
                    <Button className="btn-primary">
                      Book a Session <Calendar className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-semibold text-forest mb-4">Your Sanctuary Awaits</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dashboard?.quick_actions?.map((action, index) => (
              <Link to={action.href} key={index}>
                <Card className="card-base hover:border-forest/30 cursor-pointer h-full" data-testid={`quick-action-${index}`}>
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="h-12 w-12 rounded-xl bg-forest flex items-center justify-center mb-3">
                      {action.icon === 'users' && <Users className="h-6 w-6 text-white" />}
                      {action.icon === 'video' && <Video className="h-6 w-6 text-white" />}
                      {action.icon === 'calendar' && <Calendar className="h-6 w-6 text-white" />}
                      {action.icon === 'message' && <MessageSquare className="h-6 w-6 text-white" />}
                      {action.icon === 'clipboard' && <FileText className="h-6 w-6 text-white" />}
                      {action.icon === 'fire' && <Sparkles className="h-6 w-6 text-white" />}
                      {action.icon === 'concierge' && <Crown className="h-6 w-6 text-white" />}
                      {action.icon === 'map' && <Palmtree className="h-6 w-6 text-white" />}
                    </div>
                    <span className="font-medium text-forest text-sm">{action.label}</span>
                  </CardContent>
                </Card>
              </Link>
            )) || (
              // Default actions for no tier
              <>
                <Link to="/pathways">
                  <Card className="card-base hover:border-terracotta/30 cursor-pointer h-full">
                    <CardContent className="p-4 flex flex-col items-center text-center">
                      <div className="h-12 w-12 rounded-xl bg-terracotta flex items-center justify-center mb-3">
                        <Sparkles className="h-6 w-6 text-white" />
                      </div>
                      <span className="font-medium text-forest text-sm">Discover Your Path</span>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/coaches">
                  <Card className="card-base hover:border-forest/30 cursor-pointer h-full">
                    <CardContent className="p-4 flex flex-col items-center text-center">
                      <div className="h-12 w-12 rounded-xl bg-forest flex items-center justify-center mb-3">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <span className="font-medium text-forest text-sm">Meet Your Guides</span>
                    </CardContent>
                  </Card>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        {hasInnerCircleAccess() && dashboard?.stats && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-forest" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-forest">{dashboard.stats.communities_joined}</p>
                  <p className="text-sm text-gray-500">Your Sacred Circles</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-terracotta" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-forest">{dashboard.stats.unread_messages}</p>
                  <p className="text-sm text-gray-500">Sisters Reaching Out</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upcoming Events */}
        {hasInnerCircleAccess() && dashboard?.upcoming_events?.length > 0 && (
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">Sacred Gatherings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboard.upcoming_events.map((event, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-sand/30 rounded-lg">
                    <div className="h-12 w-12 rounded-lg bg-forest flex items-center justify-center text-white font-bold">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-forest">{event.title}</h4>
                      <p className="text-sm text-gray-500">{event.date}</p>
                    </div>
                    <Button variant="outline" size="sm" className="btn-outline">
                      Save My Seat
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upgrade Prompt for Inner Circle members */}
        {hasTier('inner_circle') && !hasTier('coaching_activation') && !hasTier('sdc') && (
          <Card className="card-base mt-8 bg-sand/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-forest">Ready to Go Deeper, Sis?</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    You've already done more than most will ever try. Your next level of transformation awaits.
                  </p>
                </div>
                <Link to="/pathways">
                  <Button className="btn-primary whitespace-nowrap">
                    Discover Your Path <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
